'use client'

import Link from 'next/link'
import { formatTimestamp } from '@/lib/timed-ui/formatting'
import Badge from '../common/Badge'


interface LocationDateRange {
  city: string
  earliest: Date
  latest: Date
}

interface SeriesData {
  id: string
  baseName: string
  count: number
  smallPic?: string
  largePic?: string
  imageUrl?: string
  eventType?: string
  venue?: string | { name?: string; venue?: string }
  venueCity?: string
  venueState?: string
  earliestDate: Date | null
  latestDate: Date | null
  locationDateRanges?: LocationDateRange[]
  slug?: string
}

interface SeriesCardProps {
  series: SeriesData
  seatedSeriesCalendar?: boolean
}

const SeriesCard = ({ series, seatedSeriesCalendar }: SeriesCardProps) => {
  const eventImage = series.smallPic || series.largePic || series.imageUrl || ''
  const href = seatedSeriesCalendar && series.eventType === 'RESERVED_SEATING'
    ? `/events/series/${encodeURIComponent(series.baseName)}`
    : `/?q=${encodeURIComponent(series.baseName)}`

  const locations = series.locationDateRanges || []
  const MAX_LOCATIONS_SHOWN = 3
  const visibleLocations = locations.slice(0, MAX_LOCATIONS_SHOWN)
  const hiddenCount = Math.max(0, locations.length - MAX_LOCATIONS_SHOWN)

  // Fallback to overall date range if no per-city data
  const dateRangeText = locations.length === 0 && series.earliestDate && series.latestDate
    ? `${formatTimestamp(series.earliestDate.getTime() / 1000, 'MMM dd')} - ${formatTimestamp(series.latestDate.getTime() / 1000, 'MMM dd, yyyy')}`
    : ''

  const venueName = typeof series.venue === 'string'
    ? series.venue
    : series.venue?.name || series.venue?.venue || ''

  const formatLocationRange = (loc: LocationDateRange) => {
    const from = formatTimestamp(loc.earliest.getTime() / 1000, 'MMM dd')
    const to = formatTimestamp(loc.latest.getTime() / 1000, 'MMM dd')
    // If same date, just show one date
    if (from === to) return from
    return `${from} - ${to}`
  }

  return (
    <Link
      href={href}
      className="bg-surface border border-surface-border rounded-xl overflow-hidden cursor-pointer group shadow-card hover:-translate-y-1 hover:shadow-card-hover hover:border-surface-border transition-all duration-200 animate-fade-in block"
    >
      <div className="relative h-48 overflow-hidden">
        {eventImage ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={eventImage}
              alt={series.baseName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-surface-tertiary relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-16 h-16 text-text-tertiary opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        )}
        <div className="absolute top-3 right-3 z-30">
          <Badge variant="primary" size="md">{series.count} dates</Badge>
        </div>
      </div>

      <div className="p-4">
        <h3 className="text-base font-heading font-semibold text-text-primary mb-2 line-clamp-2 leading-snug group-hover:text-primary transition-colors">
          {series.baseName}
        </h3>

        <div className="space-y-1.5 text-sm">
          {visibleLocations.length > 0 ? (
            <>
              {visibleLocations.map((loc) => (
                <div key={loc.city} className="flex items-start text-text-secondary">
                  <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-text-tertiary mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">
                    <span className="font-medium">{loc.city}</span>
                    <span className="text-text-tertiary"> {formatLocationRange(loc)}</span>
                  </span>
                </div>
              ))}
              {hiddenCount > 0 && (
                <div className="text-xs text-text-tertiary pl-5.5 font-medium">
                  +{hiddenCount} more {hiddenCount === 1 ? 'city' : 'cities'}
                </div>
              )}
            </>
          ) : (
            <>
              {dateRangeText && (
                <div className="flex items-center text-text-secondary">
                  <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>{dateRangeText}</span>
                </div>
              )}

              {venueName && (
                <div className="flex items-center text-text-secondary">
                  <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{venueName}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Link>
  )
}

export default SeriesCard
